import React from 'react';
import { useTranslation } from 'react-i18next';

function Projects() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'fr';
  const isInternalLink = (url) => typeof url === 'string' && url.startsWith('#');
  const autoplanDefaults = {
    fr: {
      cta: 'Demander une démo',
      description: 'Application desktop PySide6 pour générer des extraits de plan cadastraux conformes à partir de DXF, CSV ou saisies manuelles, avec cartographie Leaflet embarquée et pipeline PDF automatisé.',
      features: [
        'Import DXF, CSV et saisie manuelle des bornes avec numérotation intelligente',
        'Double visualisation : vue technique 2D + carte satellite Leaflet via QWebEngineView',
        'Calculs géométriques précis (superficie, périmètre, fermeture, conversions UTM 28N ↔ WGS84)',
        'Génération PDF ReportLab avec plan de situation, tableau des coordonnées et mentions légales',
        'Système de crédits sécurisé (1 borne = 1 crédit) avec historique chiffré et alertes de solde',
        'Packaging PyInstaller pour livrer un EXE autonome prêt pour les agents cadastraux'
      ]
    },
    en: {
      cta: 'Request a demo',
      description: 'PySide6 desktop application generating official cadastral plan extracts from DXF, CSV or manual inputs, with embedded Leaflet mapping and an automated PDF production pipeline.',
      features: [
        'DXF, CSV and manual point imports with smart vertex numbering',
        'Dual visualization: technical 2D canvas + Leaflet satellite map via QWebEngineView',
        'Accurate geometry processing (area, perimeter, polygon closure, UTM 28N ↔ WGS84 conversions)',
        'ReportLab PDF generation with situational map, coordinate table and legal notices',
        'Secure credit system (1 vertex = 1 credit) with encrypted history and balance alerts',
        'PyInstaller packaging delivering a standalone EXE for cadastral officers'
      ]
    }
  };
  const autoplanContent = t('projects.autoplan', {
    returnObjects: true,
    defaultValue: autoplanDefaults[currentLang]
  }) || autoplanDefaults[currentLang];

  const getDescription = (project) => {
    if (!project.description) return '';
    if (typeof project.description === 'string') {
      return project.description;
    }
    return project.description[currentLang] || project.description.fr || '';
  };

  const getFeatures = (project) => {
    if (!project.features) return [];
    if (Array.isArray(project.features)) {
      return project.features;
    }
    return project.features[currentLang] || project.features.fr || [];
  };

  const getCtaLabel = (project) => {
    if (!project.ctaLabel) {
      return t('projects.visit', { defaultValue: 'Visiter le site' });
    }
    if (typeof project.ctaLabel === 'string') {
      return project.ctaLabel;
    }
    return project.ctaLabel[currentLang] || project.ctaLabel.fr || t('projects.visit', { defaultValue: 'Visiter le site' });
  };

  const projects = [
    {
      id: 'parcellaire',
      title: 'Parcellaire GEOXPRT AFRIQUE',
      url: 'https://parcellairegeoxprt.web.app/',
      logo: '/LOGO- GEOXPRT AFRIQUE.png',
      description: {
        fr: 'Plateforme WebGIS complète en production sur AWS EC2 — 200 000+ parcelles géoréférencées, 22 villages, recherche universelle multi-tables. Backend Django DRF / PostgreSQL PostGIS, frontend React 19 / Leaflet / MapLibre GL.',
        en: 'Full WebGIS platform running on AWS EC2 — 200,000+ georeferenced parcels, 22 villages, universal multi-table search. Django DRF / PostgreSQL PostGIS backend, React 19 / Leaflet / MapLibre GL frontend.'
      },
      features: {
        fr: [
          '200 000+ parcelles sur 22 villages, 29 tables PostGIS couvertes',
          'Recherche universelle multi-villages avec détection dynamique des colonnes',
          'Sécurité OWASP : JWT, HSTS, Rate Limiting, CORS, DEBUG=False en production',
          'App mobile Flutter (RE-SEN) : portail client + admin panel, optimisation OOM',
          'Auto-génération IDU officiel : format {ZONE}-{NOM3}-{CNI6}',
          'Déploiement AWS EC2 eu-west-3, Nginx + Gunicorn + systemd, Firebase Hosting'
        ],
        en: [
          '200,000+ parcels across 22 villages, 29 PostGIS tables covered',
          'Universal multi-village search with dynamic column detection',
          'OWASP security: JWT, HSTS, Rate Limiting, CORS, DEBUG=False in production',
          'Flutter mobile app (RE-SEN): client portal + admin panel, OOM optimization',
          'Official IDU auto-generation: format {ZONE}-{NOM3}-{CNI6}',
          'AWS EC2 eu-west-3 deployment, Nginx + Gunicorn + systemd, Firebase Hosting'
        ]
      },
      technologies: ['Django DRF', 'PostgreSQL / PostGIS', 'React 19', 'Leaflet / MapLibre GL', 'Flutter', 'AWS EC2', 'Firebase Hosting'],
      category: 'web-gis'
    },
    {
      id: 'kalel-sa-match',
      title: 'Kalel Sa Match',
      url: '#contact',
      description: {
        fr: 'Plateforme de réservation de terrains synthétiques au Sénégal — score 97/100 à un concours académique. Géolocalisation PostGIS, paiements, commissions, analytics. Stack Laravel 12 / React 19 / Flutter / PostgreSQL PostGIS.',
        en: 'Synthetic football field booking platform in Senegal — scored 97/100 at an academic competition. PostGIS geolocation, payments, commissions, analytics. Stack: Laravel 12 / React 19 / Flutter / PostgreSQL PostGIS.'
      },
      features: {
        fr: [
          '12 terrains synthétiques recensés et géolocalisés (département de Dakar)',
          'Carte interactive Leaflet, recherche par proximité PostGIS',
          'Réservation en ligne avec OTP, paiements et gestion des commissions',
          'App mobile Flutter (Android + iOS)',
          'Dashboard analytics pour propriétaires de terrains',
          'Score 97/100 à un concours académique national'
        ],
        en: [
          '12 synthetic fields surveyed and geolocated (Dakar department)',
          'Interactive Leaflet map, PostGIS proximity search',
          'Online booking with OTP, payments and commission management',
          'Flutter mobile app (Android + iOS)',
          'Analytics dashboard for field owners',
          'Scored 97/100 at a national academic competition'
        ]
      },
      technologies: ['Laravel 12', 'React 19', 'Flutter', 'PostgreSQL / PostGIS', 'Leaflet', 'Vercel'],
      category: 'web-gis',
      ctaLabel: { fr: 'Demander une démo', en: 'Request a demo' }
    },
    {
      id: 'autoplan',
      title: 'AutoPlan GEOXPRT AFRIQUE',
      url: '#contact',
      logo: '/autoplan-logo.png',
      image: '/autoplan-ui.png',
      ctaLabel: autoplanContent.cta,
      description: autoplanContent.description,
      features: autoplanContent.features,
      technologies: ['PySide6 (Qt6)', 'Leaflet.js', 'QWebEngineView', 'ezdxf', 'pyproj', 'Shapely', 'ReportLab', 'PyInstaller'],
      category: 'desktop'
    },
    {
      id: 're-sen',
      title: 'RE-SEN',
      url: 'https://re-sen.web.app/',
      logo: '/01 LOGO RESEN.png',
      description: {
        fr: 'Marketplace et plateforme de supervision foncière : achat de parcelles, tableaux de bord interactifs, app mobile Flutter. Intégration des données cartographiques nationales.',
        en: 'Land marketplace and supervision platform: parcel purchasing, interactive dashboards, Flutter mobile app. Integration of national mapping data.'
      },
      features: {
        fr: [
          'Marketplace achat de parcelles avec filtres cartographiques',
          'Dashboard analytique avec graphiques interactifs',
          'App mobile Flutter (client + admin panel)',
          'Cartographie des zones foncières avec Mapbox',
          'Système d\'alertes et notifications',
          'Gestion multi-utilisateurs avec rôles et permissions'
        ],
        en: [
          'Parcel marketplace with map-based filters',
          'Analytical dashboard with interactive charts',
          'Flutter mobile app (client + admin panel)',
          'Land zone mapping with Mapbox',
          'Alert system and notifications',
          'Multi-user management with roles and permissions'
        ]
      },
      technologies: ['React', 'Firebase', 'Flutter', 'Mapbox', 'Node.js'],
      category: 'monitoring'
    },
    {
      id: 'demande-service',
      title: 'Demande de Service GEOXPRT AFRIQUE',
      url: 'https://demande-de-service-geoxprt.web.app/',
      logos: ['/LOGO- GEOXPRT AFRIQUE.png', '/logo_commune_de_malicounda.png'],
      description: {
        fr: 'Portail métier GEOXPRT AFRIQUE centralisant les demandes clients : formulaires intelligents, workflow de validation et notifications email.',
        en: 'GEOXPRT AFRIQUE business portal centralizing client requests: smart forms, validation workflows, and email notifications.'
      },
      features: {
        fr: [
          'Formulaire de demande de service multilingue',
          'Suivi en temps réel des demandes (statuts)',
          'Système de notification par email',
          'Gestion des documents joints',
          'Tableau de bord statistique pour les administrateurs'
        ],
        en: [
          'Multilingual service request form',
          'Real-time request tracking (status)',
          'Email notification system',
          'Attachment management',
          'Statistical dashboard for administrators'
        ]
      },
      technologies: ['React', 'Firebase', 'Material-UI', 'Firebase Cloud Messaging'],
      category: 'business'
    },
    {
      id: 'geoportail',
      title: 'Géoportail Malicounda',
      url: 'https://geoportailmalicounda.web.app/',
      logo: '/logo_commune_de_malicounda.png',
      description: {
        fr: 'Géoportail officiel de Malicounda : accès public aux infrastructures, services municipaux en ligne et outils d\'analyse spatiale pour les agents.',
        en: 'Official geoportal for Malicounda: public access to infrastructures, online municipal services, and spatial analysis tools for city officers.'
      },
      features: {
        fr: [
          'Visualisation de multiples couches cartographiques (administratives, infrastructures, environnement)',
          'Outils de mesure (distance, surface)',
          'Recherche avancée d\'adresses et points d\'intérêt',
          'Impression de cartes personnalisées',
          'Export en plusieurs formats (PDF, PNG, JPG)',
          'Mode 3D avec buildings extrudés'
        ],
        en: [
          'Visualization of multiple map layers (administrative, infrastructure, environment)',
          'Measurement tools (distance, area)',
          'Advanced address and point of interest search',
          'Custom map printing',
          'Export in multiple formats (PDF, PNG, JPG)',
          '3D mode with extruded buildings'
        ]
      },
      technologies: ['React', 'ArcGIS API', 'Mapbox', 'Deck.gl', 'Firebase'],
      category: 'geoportal'
    },
    {
      id: 'app-promoteurs',
      title: 'Portail Promoteurs GEOXPRT AFRIQUE',
      url: 'https://promoteurs-geoxprt.web.app/',
      logo: '/LOGO- GEOXPRT AFRIQUE.png',
      description: {
        fr: 'Portail cartographique individuel pour les promoteurs immobiliers : carte MapLibre GL par lotissement, fiche PDF par parcelle avec QR code de localisation, sans exposer les données clients au-delà du strict nécessaire.',
        en: "Individual mapping portal for real-estate promoters: MapLibre GL map per subdivision, per-parcel PDF sheet with a location QR code, without exposing client data beyond what's strictly needed."
      },
      features: {
        fr: [
          'Backend Django dédié (Promoteur / Lotissement / Parcelle), isolé des données clients de la plateforme principale',
          'Carte MapLibre GL par lotissement : orthophoto HD, parcelles du promoteur mises en valeur face aux parcelles voisines',
          'Fiche PDF par parcelle : numéro de lot, superficie, statut, QR code de localisation Google Maps',
          'Navigation multi-villages pour les promoteurs disposant de plusieurs lotissements',
          'Lien individuel dédié par promoteur (3 promoteurs actifs, 160+ lots)'
        ],
        en: [
          "Dedicated Django backend (Promoter / Package / Parcel), isolated from the main platform's client data",
          "MapLibre GL map per subdivision: HD orthophoto, promoter's own parcels highlighted against neighboring ones",
          'Per-parcel PDF sheet: lot number, area, status, Google Maps location QR code',
          'Multi-village navigation for promoters with several subdivisions',
          'Dedicated individual link per promoter (3 active promoters, 160+ lots)'
        ]
      },
      technologies: ['React', 'Django DRF', 'MapLibre GL', 'PostGIS', 'Firebase Hosting'],
      category: 'web-gis'
    },
    {
      id: 'idrecup',
      title: 'idRecup GEOXPRT',
      url: '#contact',
      description: {
        fr: 'Application mobile Flutter de lecture de CNI/passeport sénégalais par puce NFC et OCR, avec liaison directe aux dossiers parcellaires GeoXPRT.',
        en: 'Flutter mobile app reading Senegalese ID cards/passports via NFC chip and OCR, directly linked to GeoXPRT parcel records.'
      },
      features: {
        fr: [
          'Lecture NFC de la puce CNI/passeport (données biométriques et civiles)',
          'OCR de secours en cascade AWS Textract → Bedrock (Nova Lite puis Nova Pro)',
          "Liaison automatique de l'identité lue avec les dossiers de parcelles GeoXPRT",
          'App Flutter Android, packagée indépendamment du reste de la plateforme'
        ],
        en: [
          'NFC chip reading of ID card/passport (biometric and civil data)',
          'Cascading OCR fallback: AWS Textract → Bedrock (Nova Lite, then Nova Pro)',
          'Automatic matching of the scanned identity to GeoXPRT parcel records',
          'Standalone Flutter Android app, packaged independently from the rest of the platform'
        ]
      },
      technologies: ['Flutter', 'NFC (flutter_nfc_kit)', 'AWS Textract', 'AWS Bedrock', 'Django REST'],
      category: 'mobile',
      ctaLabel: { fr: 'Demander une démo', en: 'Request a demo' }
    }
  ];

  const renderLogos = (project) => {
    if (project.logo) {
      return (
        <img
          src={project.logo}
          alt={`${project.title} logo`}
          className="project-logo"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }

    if (project.logos) {
      return project.logos.map((logoSrc, index) => (
        <img
          key={`${project.id}-logo-${index}`}
          src={logoSrc}
          alt={`${project.title} logo ${index + 1}`}
          className="project-logo"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ));
    }

    return null;
  };

  const renderProjectMedia = (project) => {
    if (!project.image) {
      return null;
    }

    return (
      <div className="project-media">
        <img
          src={project.image}
          alt={`${project.title} interface`}
          loading="lazy"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  };

  return (
    <section className="projects-section" id="projects">
      <h3>{t('projects.title', { defaultValue: 'Projets et Réalisations' })}</h3>
      <p className="projects-intro">
        {t('projects.intro', { 
          defaultValue: 'Découvrez mes réalisations professionnelles chez GEOXPRT AFRIQUE, alliant expertise technique en géomatique et développement web moderne.' 
        })}
      </p>
      <div className="projects-note">
        {t('projects.access_notice', {
          defaultValue: 'Accès sur demande : pour préserver les données clients, merci de me solliciter avant de visiter les applications. Je vous fournirai les identifiants ou liens sécurisés nécessaires.'
        })}
      </div>
      <div className="projects-pipeline">
        <h5>{t('projects.pipeline.title', { defaultValue: 'Chaîne de production terrain → données' })}</h5>
        <ul>
          {t('projects.pipeline.steps', {
            returnObjects: true,
            defaultValue: [
              'Levés topographiques CHCNAV avec base permanente RTK couvrant tout le foncier de la commune',
              'Traitement dans BricsCAD + extension Cadarp 2000 pour structurer le parcellaire',
              'Conversion DXF → shapefile puis intégration dans PostgreSQL/PostGIS',
              'Publication et diffusion sécurisée via AWS RDS Aurora'
            ]
          }).map((step, index) => (
            <li key={`pipeline-step-${index}`}>{step}</li>
          ))}
        </ul>
      </div>
      
      <div className="projects-grid">
        {projects.map((project) => (
          <div key={project.id} className="project-card">
            <div className="project-header">
              <div className="project-logos">
                {renderLogos(project)}
              </div>
              <h4>{project.title}</h4>
              {project.url && !isInternalLink(project.url) && (
                <a 
                  href={project.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="project-link"
                >
                  🔗 Voir le projet
                </a>
              )}
            </div>
            
            <p className="project-description">
              {getDescription(project)}
            </p>

            {renderProjectMedia(project)}

            <div className="project-features">
              <h5>Fonctionnalités principales :</h5>
              <ul>
                {getFeatures(project).map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="project-tech">
              <h5>Technologies utilisées :</h5>
              <div className="tech-tags">
                {project.technologies.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>

            <div className="project-footer">
              {project.url && (
                <a 
                  href={project.url} 
                  target={isInternalLink(project.url) ? '_self' : '_blank'} 
                  rel={isInternalLink(project.url) ? undefined : 'noopener noreferrer'}
                  className="cta-button primary"
                >
                  {getCtaLabel(project)}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Projects;
